import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { axiosInstance } from '../../helpers'

export interface OrgSignableDocListItem {
    id: number
    name: string
    catalog_template_key: string
    doc_role: string
    source: string
    document_type: string
    docuseal_template_id: number | null
    created_from_template_version: number | null
    modified_date: string
}

export interface OrgSignableDocDetail extends OrgSignableDocListItem {
    docuseal_slug: string | null
    enabled_fields: string[]
    created_date: string
}

export interface OrgSignableDocApplyResult {
    created: number
    skipped_existing: number
    skipped_missing: number
    errors: number
    properties: Array<{
        property_id: number
        status: string | null
        signable_doc_id?: number
        name?: string
        detail?: string
    }>
}

const orgSignableDocsKey = (organizationId: number | null | undefined) =>
    ['org-signable-docs', organizationId] as const

export function useOrgSignableDocs(organizationId: number | null | undefined) {
    return useQuery({
        queryKey: orgSignableDocsKey(organizationId),
        queryFn: async () => {
            const { data } = await axiosInstance.get<OrgSignableDocListItem[]>(
                `/company/organization/${organizationId}/signable-docs/`,
            )
            return data
        },
        enabled: organizationId != null,
        staleTime: 30_000,
    })
}

export function useProvisionOrgSignableDoc(
    organizationId: number | null | undefined,
) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (payload: {
            catalog_template_key: string
            name?: string
        }) => {
            const { data } = await axiosInstance.post<OrgSignableDocDetail>(
                `/company/organization/${organizationId}/signable-docs/provision/`,
                payload,
            )
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: orgSignableDocsKey(organizationId),
            })
            toast.success('Org signable doc provisioned')
        },
        onError: (error: { response?: { data?: { detail?: string } } }) => {
            toast.error(
                error?.response?.data?.detail ??
                    'Failed to provision org signable doc',
            )
        },
    })
}

export function useApplyOrgSignableDocToProperties(
    organizationId: number | null | undefined,
) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (payload: {
            orgSignableDocId: number
            property_ids: number[]
            force?: boolean
        }) => {
            const {
                data,
            } = await axiosInstance.post<OrgSignableDocApplyResult>(
                `/company/organization/${organizationId}/signable-docs/${payload.orgSignableDocId}/apply/`,
                {
                    property_ids: payload.property_ids,
                    force: payload.force ?? false,
                },
            )
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: orgSignableDocsKey(organizationId),
            })
            toast.success('Signable docs applied to properties')
        },
        onError: (error: { response?: { data?: { detail?: string } } }) => {
            toast.error(
                error?.response?.data?.detail ??
                    'Failed to apply signable docs',
            )
        },
    })
}
